import { css, cx } from '@repo/pandacss/styled-system/css';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import {
  MarketplaceBankingSubProductTypeEnum,
  MarketplaceEntityTypeEnum,
} from '@repo/supergraph/marketplace/enums';
import { type DriverType } from '@repo/supergraph/marketplace/types';
import type { CSSProperties } from 'react';
import { BANNER_HEIGHT_FIXED_PX, BestOfAwardsRoundupBanner } from '../../BestOfAwards/Roundup';
import type { BestOfAwardsConfigResult } from '../../BestOfAwards/types';
import { CdApysDriver } from '../../CdApysDriver';
import { CtaButton } from '../../CtaButton';
import { NumericLayout } from '../../StarRating/NumericLayout';
import { SingleStarLayout } from '../../StarRating/SingleStarLayout';
import { summaryTableRowCdApyDriverTooltipClassName } from '../../disclosureTooltipPopover';
import { type SummaryTableRowWithDrivers } from '../types';
import { BankInstitutionCell } from './BankInstitutionCell';
import { DetailsCell } from './DetailsCell';
import { DriverCell } from './DriverCell';
import { ProductCell } from './ProductCell';
import {
  SPOTLIGHT_BADGE_HEIGHT_PX,
  SPOTLIGHT_COMPACT_HEADING_MAX_LENGTH,
  SpotlightBadge,
} from './SpotlightBadge';
import { SECTION_NAME } from './constants';

type Props = {
  index: number;
  row: SummaryTableRowWithDrivers;
  bestOfAwardsConfig?: BestOfAwardsConfigResult;
  shouldApplyStylingUpdates?: boolean;
  showMobileLayout?: boolean;
  suppressApyTermPrefix?: boolean;
};

const tableCellBaseStyles = css({
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderLeftStyle: 'none',
  borderBottomWidth: 'thin',
  borderBottomStyle: 'solid',
  borderBottomColor: 'neutral.lighter',
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
  fontWeight: 'normal',
  verticalAlign: 'top',
});

const centeredCellStyles = css({
  verticalAlign: 'middle',
  textAlign: 'center',
});

const centeredInstitutionCellStyles = css({
  textAlign: 'center',
  verticalAlign: 'middle',
});

const ratingContainerStyles = css({
  alignItems: 'center',
  justifyContent: 'center',
  height: 'full',
});

export default function SummaryTableRow({
  index,
  row,
  bestOfAwardsConfig,
  shouldApplyStylingUpdates = false,
  showMobileLayout = false,
  suppressApyTermPrefix = false,
}: Props) {
  const {
    categoryName,
    oneYearApyEntity,
    threeYearApyEntity,
    fiveYearApyEntity,
    hideBestOfAwardsBanner = false,
    spotlightHeading,
    spotlightDetails,
    marketplaceEntity: {
      displayName,
      drivers,
      entityType,
      id,
      image,
      institution,
      monetizable: defaultMonetizable,
      name,
      offerForCta,
      product,
      productType,
      reviewLink,
      starRating,
      starRatingTooltip,
      subProductType,
      ctaOverride,
      cta,
    },
  } = row;
  const monetizable = offerForCta?.monetizable ?? defaultMonetizable!;

  const offerId = offerForCta?.id ? offerForCta.id : id;
  const offerName = offerForCta?.name ? offerForCta.name : name;

  const analyticsPayload = {
    index,
    ...(institution?.id && { institutionId: institution.id }),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };

  const isCDSubproductTypes = subProductType === MarketplaceBankingSubProductTypeEnum.CD;

  const hasBestOfAward = (bestOfAwardsConfig?.hasBestOfAward ?? false) && !hideBestOfAwardsBanner;
  const bestOfAwardsConfigData = bestOfAwardsConfig?.config;

  const hasSpotlight = Boolean(spotlightHeading && spotlightDetails);
  const hasTopBadge = hasSpotlight || hasBestOfAward;

  const themedSpacing = '1rem';

  const badgeHeightPx = hasSpotlight ? SPOTLIGHT_BADGE_HEIGHT_PX : BANNER_HEIGHT_FIXED_PX;
  const cellPaddingTop = hasTopBadge
    ? `calc(${badgeHeightPx}px + ${themedSpacing})`
    : themedSpacing;

  const mobileCellDisplay = showMobileLayout
    ? css({
      borderBottomWidth: 'thin',
      borderBottomStyle: 'solid',
      borderBottomColor: 'neutral.lighter2',
        display: { base: 'table-cell', sm: 'none' },
      })
    : undefined;
  const desktopCellDisplay = showMobileLayout
    ? css({ display: { base: 'none', sm: 'table-cell' } })
    : undefined;

  const isCompactSpotlight =
    hasSpotlight && (spotlightHeading?.length ?? 0) <= SPOTLIGHT_COMPACT_HEADING_MAX_LENGTH;

  const awardBannerContentWidth = showMobileLayout ? '100%' : 'max-content';
  const spotlightContentWidth = 'fit-content';

  const spotlightCellPositionStyles =
    hasSpotlight && isCompactSpotlight && css({ position: 'relative' });

  const beforeMainContent =
    hasTopBadge &&
    (hasSpotlight ? (
      <div
        className={cx(
          css({
            position: 'absolute',
            left: '0',
            top: '0',
            width: 'var(--summary-table-row-before-content-width)',
            zIndex: 1,
          }),
          isCompactSpotlight ? css({ minWidth: 'full' }) : css({ maxWidth: 'full' }),
        )}
        style={
          {
            '--summary-table-row-before-content-width': spotlightContentWidth,
          } as React.CSSProperties
        }
      >
        <SpotlightBadge
          heading={spotlightHeading!}
          variant={isCompactSpotlight ? 'compact' : 'full'}
        />
      </div>
    ) : (
      bestOfAwardsConfigData && (
        <div
          className={css({
            position: 'absolute',
            left: '0',
            top: '0',
            width: 'var(--summary-table-row-before-content-width)',
            zIndex: 1,
          })}
          style={
            {
              '--summary-table-row-before-content-width': awardBannerContentWidth,
            } as React.CSSProperties
          }
        >
          <BestOfAwardsRoundupBanner variant={showMobileLayout ? 'row-compact' : 'row'}>
            {bestOfAwardsConfigData.awardText}
          </BestOfAwardsRoundupBanner>
        </div>
      )
    ));

  let mobilePaddingTop = themedSpacing;
  if (hasTopBadge && !showMobileLayout) {
    mobilePaddingTop = `calc(${badgeHeightPx}px + ${themedSpacing})`;
  } else if (hasTopBadge) {
    mobilePaddingTop = `${badgeHeightPx}px`;
  } else if (showMobileLayout) {
    mobilePaddingTop = '0';
  }

  return (
    <AnalyticsSkuViewed
      source={SEGMENT_SKU_VIEWED_SOURCE}
      name={offerName}
      productType={productType}
      level="offer"
      {...(institution && {
        institutionId: institution.id,
        institutionName: institution.name,
      })}
      {...(entityType === MarketplaceEntityTypeEnum.Institutions && {
        institutionId: id,
        institutionName: name,
      })}
      {...(product && {
        productId: product.id,
        productName: product.name,
      })}
      offerId={offerId}
      offerName={offerName}
      monetizable={monetizable}
      impressionSection={SECTION_NAME}
      impressionPosition={index}
      additionalAttributes={{
        page_type: 'roundup',
      }}
    >
      <tr
        id={`summary-table-row-${index}`}
        className={css({
          scrollMarginBottom: '[65px]',
          position: 'var(--summary-table-row-position)',
          '&:last-of-type td': {
            borderBottomWidth: 'thin',
            borderBottomStyle: 'solid',
            borderBottomColor: 'neutral.lighter',
          },
        })}
        style={
          {
            '--cell-padding-top': cellPaddingTop,
            '--mobile-cell-padding-top': mobilePaddingTop,
            '--summary-table-row-position': hasTopBadge ? 'sticky' : 'static',
          } as React.CSSProperties
        }
      >
        {showMobileLayout && (
          <>
            <ProductCell
              beforeMainContent={beforeMainContent}
              analyticsPayload={analyticsPayload}
              marketplaceEntity={row.marketplaceEntity}
              className={cx(
                mobileCellDisplay,
                css({ lineHeight: '[1.43]' }),
                spotlightCellPositionStyles,
              )}
            />
            <DetailsCell
              drivers={drivers}
              className={cx(
                mobileCellDisplay,
                css({
                  paddingInlineStart: '4',
                  paddingInlineEnd: '4',
                }),
              )}
            />
          </>
        )}
        <>
          <td
            className={cx(
              tableCellBaseStyles,
              desktopCellDisplay,
              css({
                minWidth: { md: '[190px]' },
                width: { base: '[190px]', md: 'var(--cell-md-width)' },
                paddingBlockStart: '4',
                paddingBlockEnd: '4',
                paddingTop: 'var(--cell-padding-top)',
                paddingLeft: '0',
                paddingRight: '2',
                backgroundColor: 'white',
              }),
              hasBestOfAward &&
                !showMobileLayout &&
              css({
                overflowX: 'visible',
                overflowY: 'visible',
                position: 'relative',
              }),
              spotlightCellPositionStyles,
              shouldApplyStylingUpdates && centeredInstitutionCellStyles,
            )}
            style={
              {
                '--cell-padding-top': cellPaddingTop,
                '--cell-md-width': drivers?.length ? '190px' : 'auto',
              } as React.CSSProperties
            }
          >
            {beforeMainContent}
            <BankInstitutionCell
              image={image}
              cta={ctaOverride ?? cta ?? null}
              analyticsPayload={analyticsPayload}
              displayName={displayName}
              shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            />
          </td>
          <td
            className={cx(
              tableCellBaseStyles,
              desktopCellDisplay,
              css({
                paddingBlockStart: '4',
                paddingBlockEnd: '4',
                paddingTop: 'var(--cell-padding-top)',
                paddingInlineStart: '2',
                paddingInlineEnd: '2',
                width: '[230px]',
                minWidth: '[130px]',
              }),
              shouldApplyStylingUpdates && centeredCellStyles,
            )}
            style={
              {
                '--cell-padding-top': cellPaddingTop,
              } as React.CSSProperties
            }
          >
            <div
              className={cx(
                css({
                  display: 'flex',
                  flexDirection: 'column',
                }),
                shouldApplyStylingUpdates && ratingContainerStyles,
              )}
            >
              {shouldApplyStylingUpdates ? (
                <SingleStarLayout
                  starRating={starRating}
                  starRatingTooltip={starRatingTooltip}
                  reviewLink={reviewLink}
                />
              ) : (
                <NumericLayout
                  starRating={starRating}
                  reviewLink={reviewLink}
                  hideLabel
                  iconSize={17}
                />
              )}
              {!!categoryName && (
                <span
                  className={css({
                    textStyle: 'textSmall',
                    color: 'neutral.dark',
                  })}
                >
                  {categoryName}
                </span>
              )}
            </div>
          </td>
          {drivers?.map((driver: DriverType, driverIndex: number) => {
            return (
              <td
                key={driverIndex}
                className={cx(
                  tableCellBaseStyles,
                  desktopCellDisplay,
                  css({
                    paddingBlockStart: '4',
                    paddingBlockEnd: '4',
                    paddingTop: 'var(--cell-padding-top)',
                    paddingInlineStart: '2',
                    paddingInlineEnd: '2',
                    width: '[190px]',
                    minWidth: '[130px]',
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    textOverflow: 'ellipsis',
                  }),
                  shouldApplyStylingUpdates && centeredCellStyles,
                )}
              >
                {driver?.id?.includes('apy') && isCDSubproductTypes ? (
                  <CdApysDriver
                    driver={driver}
                    oneYearApyEntity={oneYearApyEntity}
                    threeYearApyEntity={threeYearApyEntity}
                    fiveYearApyEntity={fiveYearApyEntity}
                    tooltipClassName={summaryTableRowCdApyDriverTooltipClassName}
                    suppressApyTermPrefix={suppressApyTermPrefix}
                    shouldApplyStylingUpdates={shouldApplyStylingUpdates}
                    iconSize={14}
                  />
                ) : (
                  <DriverCell driver={driver} />
                )}
              </td>
            );
          })}

          <td
            className={cx(
              tableCellBaseStyles,
              css({
                width: '[159px]',
                paddingBlockStart: '4',
                paddingBlockEnd: '4',
                paddingTop: 'var(--cell-padding-top)',
                paddingInlineStart: '2',
                paddingInlineEnd: '2',
                // always set the display and not desktopCellDisplay since it relies on the mobile layout
                display: { base: 'none', md: 'table-cell' },
              }),
              shouldApplyStylingUpdates && centeredCellStyles,
            )}
          >
            <div
              className={css({
                display: { base: 'none', md: 'block' },
                width: '[159px]',
                marginInlineStart: 'var(--summarytablerow-cta-margin-x)',
                marginInlineEnd: 'var(--summarytablerow-cta-margin-x)',
              })}
              style={
                {
                  '--summarytablerow-cta-margin-x': shouldApplyStylingUpdates ? 'auto' : '0',
                } as CSSProperties
              }
            >
              {(ctaOverride ?? cta) && (
                <CtaButton
                  className={css({
                    columnGap: '0',
                    rowGap: '0',
                  })}
                  cta={ctaOverride ?? cta}
                  analyticsPayload={analyticsPayload}
                  buttonClassName={css({
                    height: { base: '[29px]', md: '[45px]' },
                  })}
                  subtextContainerClassName={css({
                    lineHeight: 'md',
                  })}
                  subtextClassName={css({
                    textAlign: 'center',
                    marginTop: '0',
                    marginRight: '0',
                    marginBottom: '0',
                    marginLeft: '0',
                  })}
                />
              )}
            </div>
          </td>
        </>
      </tr>
    </AnalyticsSkuViewed>
  );
}
